class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.lastTrackedSearchKey = '';
    this.lastTrackedSearchResultKey = '';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  getAnalyticsSearchTerm(query = this.getQuery()) {
    const term = String(query || '').trim().replace(/\s+/g, ' ').slice(0, 100);
    if (term.length < 2) return '';
    if (/@/.test(term) || /\b\d[\d\s().+-]{6,}\d\b/.test(term)) return '';
    return term;
  }
  getSearchLocation() {
    return this.closest('.template-search') ? 'search_page' : 'drawer';
  }
  pushAnalyticsEvent(eventName, payload = {}) {
    if (!eventName || typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  }
  getSearchResultCounts(root) {
    const queryResultCount = root.querySelectorAll('#predictive-search-results-queries-list [role="option"]').length;
    const productResultCount = root.querySelectorAll('#predictive-search-results-products-list [role="option"]').length;
    const collectionResultCount = root.querySelectorAll('#predictive-search-results-collection-list [role="option"]').length;
    const articlePageResultCount = root.querySelectorAll('#predictive-search-results-article-page-list [role="option"]').length;
    const resultCount = queryResultCount + productResultCount + collectionResultCount + articlePageResultCount;

    return {
      result_count: resultCount,
      product_result_count: productResultCount,
      query_result_count: queryResultCount,
      collection_result_count: collectionResultCount,
      has_results: resultCount > 0,
    };
  }
  trackSearchIntent(query = this.getQuery()) {
    const searchTerm = this.getAnalyticsSearchTerm(query);
    if (!searchTerm) return;

    const payload = {
      search_term: searchTerm,
      search_location: this.getSearchLocation(),
    };
    const searchKey = `${payload.search_location}|${payload.search_term}`;
    if (this.lastTrackedSearchKey === searchKey) return;

    this.lastTrackedSearchKey = searchKey;
    this.pushAnalyticsEvent('search', payload);
  }
  trackSearchResults(root) {
    const searchTerm = this.getAnalyticsSearchTerm();
    if (!searchTerm || !root) return;

    const payload = {
      search_term: searchTerm,
      search_location: this.getSearchLocation(),
      ...this.getSearchResultCounts(root),
    };
    const resultKey = [
      payload.search_location,
      payload.search_term,
      payload.result_count,
      payload.product_result_count,
      payload.query_result_count,
      payload.collection_result_count,
      payload.has_results ? '1' : '0',
    ].join('|');

    if (this.lastTrackedSearchResultKey === resultKey) return;

    this.lastTrackedSearchResultKey = resultKey;
    this.pushAnalyticsEvent('view_search_results', payload);
    if (!payload.has_results) this.pushAnalyticsEvent('duori_search_no_results', payload);
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    const query = this.getQuery();

    if (query.length === 0) {
      this.clear();
      return;
    }
    this.trackSearchIntent(query);
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.trackSearchResults(targetElement);
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
